import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const services = [
  {
    key: "crudeOil",
    icon: "ri-oil-line",
    img: "https://public.readdy.ai/ai/img_res/37010650b504444855ee84584de3b1b2.jpg",
  },
  {
    key: "lng",
    icon: "ri-fire-line",
    img: "https://readdy.ai/api/search-image?query=LNG%20liquefied%20natural%20gas%20terminal%20with%20large%20cryogenic%20storage%20tanks%20at%20dusk%2C%20industrial%20energy%20facility%2C%20professional%20photography%2C%20clean%20background&width=800&height=500&seq=srv102&orientation=landscape",
  },
  {
    key: "market",
    icon: "ri-line-chart-line",
    img: "https://readdy.ai/api/search-image?query=energy%20market%20data%20analytics%20dashboard%20with%20multiple%20screens%20showing%20oil%20price%20charts%20and%20global%20energy%20data%2C%20professional%20trading%20environment%2C%20modern%20office&width=800&height=500&seq=srv103&orientation=landscape",
  },
  {
    key: "contract",
    icon: "ri-file-text-line",
    img: "https://readdy.ai/api/search-image?query=professional%20business%20contract%20negotiation%20meeting%20with%20executives%20at%20conference%20table%20reviewing%20documents%2C%20formal%20corporate%20setting%2C%20natural%20light%2C%20clean%20modern%20office&width=800&height=500&seq=srv104&orientation=landscape",
  },
  {
    key: "risk",
    icon: "ri-shield-check-line",
    img: "https://readdy.ai/api/search-image?query=global%20energy%20supply%20chain%20map%20with%20shipping%20routes%20and%20logistics%20network%2C%20world%20map%20with%20energy%20infrastructure%20connections%2C%20professional%20business%20visualization&width=800&height=500&seq=srv105&orientation=landscape",
  },
  {
    key: "regulatory",
    icon: "ri-global-line",
    img: "https://readdy.ai/api/search-image?query=professional%20legal%20and%20compliance%20team%20reviewing%20energy%20regulations%20and%20documents%2C%20corporate%20law%20office%20setting%2C%20formal%20business%20environment%2C%20clean%20modern%20interior&width=800&height=500&seq=srv106&orientation=landscape",
  },
];

export default function ServicesPage() {
  const { t } = useTranslation(["pages"]);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=oil%20and%20gas%20industrial%20infrastructure%20panoramic%20view%2C%20refinery%20and%20LNG%20facility%20combined%2C%20professional%20aerial%20photography%2C%20dramatic%20sky%2C%20wide%20angle%20cinematic&width=1920&height=600&seq=svchero&orientation=landscape"
            alt="Services"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
            {t("pages:services.hero.subtitle")}
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:services.hero.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("pages:services.hero.description")}
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-20">
          {services.map((svc, i) => (
            <div
              key={svc.key}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="w-full h-72 rounded-xl overflow-hidden">
                  <img
                    src={svc.img}
                    alt={t(`pages:services.items.${svc.key}.title`)}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 mb-4">
                  <i className={`${svc.icon} text-xl`} />
                </div>
                <h2
                  className="text-2xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {t(`pages:services.items.${svc.key}.title`)}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {t(`pages:services.items.${svc.key}.desc`)}
                </p>
                <ul className="space-y-2">
                  {t(`pages:services.items.${svc.key}.features`, { returnObjects: true }).map(
                    (feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-3 text-sm text-slate-700">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-emerald-600" />
                        </div>
                        {feature}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-800 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:services.cta.title")}
          </h2>
          <p className="text-emerald-200 mb-8">
            {t("pages:services.cta.description")}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-emerald-800 font-semibold px-8 py-3.5 rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("pages:services.cta.button")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}